/**
 * ChatFeed · 对话消息流容器
 *
 * 自动滚动（v4.6 · 修复 streaming 竞态）
 * ───────────────────────────────────────────────
 * 触发滚到底场景：
 *  1. messages 数量增加（新消息追加，包括"AI 思考中..."的 streaming 壳）
 *  2. 容器尺寸变小（底部 BottomOptions/QuickPrompts 出现时）
 *  3. 同一条消息内容增长（streaming 累加 / 卡片渲染让消息高度变高）—— 仅在用户仍贴底时滚
 *  4. 用户主动向上回看时仍能停留（streaming/同消息变化不抢回视口）
 *
 * 实现关键
 *  · useLayoutEffect：paint 前同步滚，避免视觉跳动
 *  · ResizeObserver：监听容器自身高度（底部按钮区出现 → feed 高度缩小）
 *    + 监听 list 内部尺寸（消息 streaming/卡片渲染时高度变化）
 *  · prevMessageCountRef：识别"是否新消息"，新消息总是强制滚到底
 *
 * v4.6 修复
 * ───────────────────────────────────────────────
 * v4.5 有个隐蔽竞态：程序 scrollToBottom 把 scrollTop 设到当时 scrollHeight，
 * scroll 事件**异步**触发；handleScroll 跑的时候 streaming 内容已经又涨高了，
 * 此时 scrollTop + clientHeight < 新 scrollHeight - 40 被判定成"用户已离底"，
 * stickToBottomRef.current = false，导致后续 ResizeObserver 的内容增长事件不再滚底。
 * 表现：用户发完消息后，AI streaming 出来的内容不会自动跟到底。
 *
 * 修复：scrollToBottom 时打一个时间戳，handleScroll 在 120ms 窗口内忽略不更新
 * stickToBottom（避免被涨内容期间的"假离底"误判）。
 */

import React, { useEffect, useLayoutEffect, useRef } from 'react'
import { ReloadOutlined } from '@ant-design/icons'
import { useAppSelector } from '../../../../../../app/hooks'
import {
  selectAllMessages,
  selectSessionStatus
} from '../../../../../../slices/aiClassify'
import { useChatActions } from '../../ChatActionsContext'
import MessageRenderer from './MessageRenderer'
import SparkleIcon from '../../../SparkleIcon'
import RecommendCards from '../../../RecommendCards'
import { useRecommendCards } from '../../../RecommendCards/useRecommendCards'
import TutorialBanner from '../../../TutorialBanner'
import { openClassifyTutorial } from '../../../../utils/classifyTutorial'
import styles from './ChatFeed.module.scss'

/** 贴底判定阈值 · scrollTop + clientHeight 离底 ≤ 40px 视为贴底 */
const STICK_THRESHOLD_PX = 40

/**
 * 程序 scroll 后忽略 handleScroll 的窗口（ms）
 * 取 120ms 是因为 streaming 内容通常 1 帧（16ms）就能涨完一波，
 * 但为了兼容 SSE 多事件连续到达 + React 多次 commit，留 ~7 帧缓冲。
 */
const PROGRAMMATIC_SCROLL_GRACE_MS = 120

const ChatFeed: React.FC = () => {
  const messages = useAppSelector(selectAllMessages)
  const sessionStatus = useAppSelector(selectSessionStatus)
  const { sendUserMessage } = useChatActions()
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const prevMessageCountRef = useRef(0)
  /** 上一次容器高度 · 用于判断是否"缩小"（底部按钮区出现） */
  const prevContainerHeightRef = useRef(0)
  /**
   * 是否"贴底" · 用 ref 而非 state：
   *  · ResizeObserver / scroll 回调要同步读取最新值
   *  · 不需要因为 stick 状态变化触发渲染
   */
  const stickToBottomRef = useRef(true)
  /**
   * 上一次程序 scroll 的时间戳 · 用来过滤随后异步触发的 scroll 事件，
   * 避免"内容继续涨"被误判成"用户已离底"
   */
  const lastProgrammaticScrollAtRef = useRef(0)

  const isEmpty = messages.length === 0

  // 推荐卡当前批 + 「换一批」轮换（v2 init 数据，loading 时先不渲染卡片区）
  const {
    cards: recommendCards,
    loading: recommendCardsLoading,
    canRefresh: canRefreshRecommend,
    refreshGroup
  } = useRecommendCards()

  /** 「换一批」· 向后翻一批推荐卡，翻完循环回第 1 批 */
  const handleRefresh = (): void => {
    refreshGroup()
  }

  /** 「怎么用 AI 批量分类」教程入口 · 与顶部 TutorialBanner 共用跳转（URL 占位一处维护） */
  const handleOpenTutorial = (): void => {
    openClassifyTutorial()
  }

  const handleScroll = (): void => {
    // 程序 scroll 后的 grace 窗口内，忽略 handleScroll 的离底判定
    // （streaming 内容仍在涨高时，scroll 事件读到的 scrollHeight 已经比设值时大，
    //  会被错误判成"用户离底"→ stickToBottomRef 翻 false → 后续 ResizeObserver 不再滚）
    if (
      performance.now() - lastProgrammaticScrollAtRef.current <
      PROGRAMMATIC_SCROLL_GRACE_MS
    ) {
      return
    }
    const el = containerRef.current
    if (!el) return
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - STICK_THRESHOLD_PX
    stickToBottomRef.current = atBottom
  }

  // 用户主动手势（wheel / touch）→ 立即按真实 scrollTop 重判 stick，**不走 grace
  // 窗口**。
  //
  // bad case 2026-05-07：用户在 streaming 大量 folder 时想往上回看，发现根本滚
  // 不动 / 立刻被拉回底部。根因 — streaming 内容持续 ResizeObserver → 持续
  // scrollToBottom → 持续刷新 lastProgrammaticScrollAtRef → handleScroll 每次
  // 都落在 grace 内被吞 → stickToBottomRef 永远 true → 用户的"向上滚"被下一波
  // ResizeObserver 立刻拉回底。
  //
  // wheel/touchmove 是用户**真实意图**，跟 grace 路径解耦：直接读 scrollTop
  // 判定离底，离底就 stickToBottomRef=false 让 ResizeObserver 别再抢回视口。
  // 用户自己滚回底（atBottom=true）才恢复 sticky。
  const handleUserGesture = (): void => {
    const el = containerRef.current
    if (!el) return
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - STICK_THRESHOLD_PX
    stickToBottomRef.current = atBottom
  }

  /** 强制滚到底 · 抽出来给多处复用 */
  const scrollToBottom = (): void => {
    const el = containerRef.current
    if (!el) return
    lastProgrammaticScrollAtRef.current = performance.now()
    el.scrollTop = el.scrollHeight
  }

  // 消息变化 → 决定是否滚底
  // useLayoutEffect 确保在 paint 前完成滚动，避免一闪而过的"看到旧底"
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return

    const prevCount = prevMessageCountRef.current
    const isNewMessage = messages.length > prevCount
    prevMessageCountRef.current = messages.length

    // 新消息 → 强制滚底（包括 "AI 思考中..." streaming 壳）
    // 同消息内容增长 → 仅贴底时滚（不抢回向上回看的用户视口）
    if (isNewMessage || stickToBottomRef.current) {
      scrollToBottom()
    }
  }, [messages])

  // 容器尺寸观察：
  //  · 容器自身变小（底部按钮区出现挤压）→ 强制滚底
  //    （用户之前看到的最后一条可能被新出现的底部按钮区遮挡）
  //  · 容器自身变大（按钮收起）→ 仅贴底时滚
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    prevContainerHeightRef.current = el.clientHeight

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newHeight = entry.contentRect.height
        const prevHeight = prevContainerHeightRef.current
        prevContainerHeightRef.current = newHeight

        // 容器变小（>4px 阈值过滤抖动）→ 强制滚底
        if (newHeight < prevHeight - 4) {
          scrollToBottom()
        } else if (stickToBottomRef.current) {
          // 变大 / 不变时，只在贴底状态滚（不抢用户视口）
          scrollToBottom()
        }
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // 列表内容观察：
  //  · 内部 list 高度变化（streaming 文本累加 / 卡片渲染补齐）
  //    → 只在贴底时滚（不抢回视口）
  useEffect(() => {
    const list = listRef.current
    if (!list) return

    const observer = new ResizeObserver(() => {
      if (stickToBottomRef.current) {
        scrollToBottom()
      }
    })
    observer.observe(list)
    return () => observer.disconnect()
  }, [isEmpty])

  return (
    <div
      ref={containerRef}
      className={styles.feed}
      onScroll={handleScroll}
      onWheel={handleUserGesture}
      onTouchMove={handleUserGesture}
      role="log"
      aria-live="polite"
    >
      {/* 新手教程入口 · 滚动区内首位 + sticky 浮动固定在顶部：初始不遮挡
          第一屏，滚动时消息从其下方穿过（下边缘白色渐变柔化）；
          显隐条件组件内部自管（用户级缓存 + 团队用量） */}
      <TutorialBanner />
      {isEmpty ? (
        // 空会话：居中 hero + 标题 / 副标题 + 推荐卡片 + 动作行（换一批 / 教程）
        // 推荐卡片来自 v2 init recommend_cards（useRecommendCards 取组 + 兜底）
        <div className={styles.empty}>
          <div className={styles.welcomeHero} aria-hidden="true">
            <SparkleIcon />
          </div>
          <h2 className={styles.welcomeTitle}>让 AI 帮你整理照片</h2>
          <p className={styles.welcomeSubtitle}>选择一种分类方式开始</p>
          {!recommendCardsLoading && (
            <RecommendCards
              cards={recommendCards}
              onSelectCard={sendUserMessage}
              disabled={sessionStatus === 'streaming'}
            />
          )}
          <div className={styles.actions}>
            {/* 「换一批」仅在有下一批（>4 张）时渲染，同时带走后面的分隔竖线 */}
            {canRefreshRecommend && (
              <>
                <button
                  type="button"
                  className={styles.actionBtn}
                  onClick={handleRefresh}
                  disabled={sessionStatus === 'streaming'}
                >
                  <ReloadOutlined
                    className={styles.actionIcon}
                    aria-hidden="true"
                  />
                  换一批
                </button>
                <span className={styles.actionSep} aria-hidden="true" />
              </>
            )}
            <button
              type="button"
              className={styles.actionLink}
              onClick={handleOpenTutorial}
            >
              怎么用 AI 批量分类？
            </button>
          </div>
        </div>
      ) : (
        <div ref={listRef} className={styles.list}>
          {messages.map((m) => (
            <MessageRenderer key={m.id} message={m} />
          ))}
        </div>
      )}
    </div>
  )
}

export default ChatFeed
